import { randomNumericString } from '@utils/random';
import { useRouter } from 'next/router';
import { FunctionComponent, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { SpaceBetweenRowContainer } from '../container/common';
import { StyledElement } from '../types';

const MenuItemStyled = styled.li`
  &::before {
    padding-right: 0;
    display: none;
  }
`;

const SubmenuStyled = styled.ul<{ close?: boolean }>`
  ${({ close }) =>
    close &&
    css`
      @media (max-width: 768px) {
        display: none !important;
      }
    `}
`;

export type MenuProps = {
  title: React.ReactNode;
  items: {
    content: React.ReactNode;
  }[];
} & StyledElement;

const Title = styled(SpaceBetweenRowContainer)`
  width: 100%;
`;

const MenuStyled = styled.div`
  * {
    cursor: pointer;
  }
  input {
    position: absolute;
    opacity: 0;
    z-index: -1;
  }

  label {
    width: 100%;
  }

  ul {
    list-style: none;
    margin: 0;
    li,
    li a {
      color: #202223;
      cursor: pointer;
      text-decoration: none;
      white-space: nowrap;
      font-weight: 400;
      &:hover {
        opacity: 1;
      }
      a {
        display: flex;
        align-items: center;
        height: 100%;
        width: 100%;
      }
    }

    li {
      i {
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 5px solid ${({ theme }) => theme.colors.chevron.hex};
        margin-left: 10px;
      }
    }
  }
  > ul {
    display: flex;
    padding: 0;
    align-items: center;
    :hover {
      background-color: ${({ theme }) => theme.colors.divider.hex};
    }
    > li {
      height: 40px;
      padding: 10px;
    }

    li {
      position: relative;
      margin: 0;
      ul {
        visibility: hidden;
        opacity: 0;
        padding: 0;
        min-width: 160px;
        background-color: white;
        position: absolute;
        top: 50px;
        left: 10px;
        transition: 150ms;
        border: 1px solid ${({ theme }) => theme.colors.divider.hex};
        border-radius: 10px;

        li {
          margin: 0;
          padding: 10px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          height: 30px;
          i {
            border-top: 5px solid transparent;
            border-bottom: 5px solid transparent;
            border-left: 5px solid ${({ theme }) => theme.colors.chevron.hex};
          }
          ul {
            top: -2%;
            left: 105%;
            transform: translate(0);
          }
          &:hover {
            background-color: ${({ theme }) => theme.colors.divider.hex};
            :first-child {
              border-top-left-radius: 10px;
              border-top-right-radius: 10px;
            }

            :last-child {
              border-bottom-left-radius: 10px;
              border-bottom-right-radius: 10px;
            }
          }
        }
      }
      &:hover {
        > ul {
          opacity: 1;
          visibility: visible;
          transition-delay: 0ms;
        }
      }
    }
  }
  @media (max-width: 768px) {
    z-index: 99;
    ul {
      list-style: none;
      padding: 10px;
      margin: 0;
    }
    > ul {
      display: flex;
      height: 40px;
      align-items: center;
      :hover {
        background-color: ${({ theme }) => theme.colors.divider.hex};
      }
      li {
        position: relative;
        margin: 0;
        ul {
          padding: 0;
          width: 100vw;
          background-color: white;
          position: absolute;
          top: 41px;
          left: -10px;
          transition: 150ms;
          border: none;
          border-bottom: 1px solid ${({ theme }) => theme.colors.divider.hex};
          border-radius: 0;

          li {
            margin: 0;
            padding: 12px 10px;
            display: block;
            height: auto;
            i {
              border-left: 5px solid transparent;
              border-right: 5px solid transparent;
              border-top: 5px solid ${({ theme }) => theme.colors.chevron.hex};
              margin-right: 10px;
            }
            ul {
              top: 0;
              left: 0;
              transform: none;
              position: relative;
              display: none;
              margin-top: 10px;
              border: none;

              li {
                :last-child {
                  padding-bottom: 0;
                }
              }
            }
            &:hover {
              background-color: white;
              :first-child {
                border-top-left-radius: 0;
                border-top-right-radius: 0;
              }

              :last-child {
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
              }
            }
          }
        }

        &:hover {
          > ul {
            opacity: 1;
            visibility: visible;
            display: inherit;
            transition-delay: 0ms;
          }
        }
      }
    }
  }
`;

export const MenuItem: FunctionComponent<{ key?: string }> = ({
  children,
  key,
}) => {
  return <MenuItemStyled key={key}>{children}</MenuItemStyled>;
};

export const Submenu: FunctionComponent<MenuProps> = ({
  className,
  items,
  title,
}) => {
  const id = randomNumericString(6);
  return (
    <li className={className}>
      <label htmlFor={id}>
        <Title>
          <span>{title}</span>
          <i />
        </Title>
      </label>
      <input id={id} type="radio" name="submenu" />
      <ul>{items.map(({ content }) => content)}</ul>
    </li>
  );
};

const Menu: FunctionComponent<MenuProps> = ({ className, items, title }) => {
  const [close, setClose] = useState(false);
  const id = randomNumericString(6);
  const router = useRouter();
  useEffect(() => {
    const handleStart = () => {
      setClose(true);
    };

    router.events.on('routeChangeStart', handleStart);

    return () => {
      router.events.off('routeChangeStart', handleStart);
    };
  }, [router]);

  return (
    <MenuStyled className={className} onClick={() => setClose(false)}>
      <ul>
        <li>
          <label htmlFor={id}>
            <Title>
              <span>{title}</span>
              <i />
            </Title>
          </label>
          <input id={id} type="checkbox" />
          <SubmenuStyled close={close}>
            {items.map(({ content }) => content)}
          </SubmenuStyled>
        </li>
      </ul>
    </MenuStyled>
  );
};

export default Menu;
